import React from 'react';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import { analytics } from 'core/analytics';
import { staticURL } from 'core/url';

import Header from 'apps/components/header/header';
import Paragraph from 'components/Paragraph';
import { ContentIntro } from 'apps/components/contentIntro';
import DocumentHead from 'legacyComponents/documentHead';
import Join from 'apps/home/landing/sections/join';

import { Container, HeadingStyled, ImageStyled } from '../company.styled';

@withNamespaces()
@inject('routerStore', 'clientStore')
@observer
export class OurStory extends React.Component {
  constructor() {
    super();
    analytics.sendEvent('ViewContent', {}, { twitter: true, facebook: true });
    analytics.linkedinPixel('1169129');
  }

  render() {
    const { clientStore } = this.props;
    return (
      <>
        <DocumentHead imagePath={staticURL('assets/wikifactory.jpg')} />
        <Header isDark />
        <ContentIntro
          title='Our Story'
          hideButtonWhenSignIn={true}
          variant='secondary'
          isCentered
        />
        <Container>
          <HeadingStyled level={2} size='large'>
            <Trans>It all started with a big...</Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            {/* TODO: add this source responsive images control for the image component */}
            <picture>
              <ImageStyled src={staticURL('/assets/home/what-if.jpg')} />
            </picture>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>What if there were a Wikipedia for physical things?</Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            <Trans>
              A place where everyone could openly take part in producing and
              sharing physical products with just a laptop and an internet
              connection? Not just useful knowledge, but useful innovation? What
              if there was a place with recipes to build products that could be
              immediately shared, downloaded, improved upon, and made into
              things that people actually need?
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              When we started thinking about creating this space six years ago,
              digital fabrication had only just begun to emerge. And with it, a
              distributed community began to grow across the world. They adopted
              new manufacturing technologies to change how things were made. And
              they started sharing their ideas online. From precision-cut
              furniture, to customised CNC milled homes, to affordable science
              equipment, prosthetics, and more. Today, there isn&apos;t an
              industry that hasn&apos;t been touched by these new manufacturing
              technologies.
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              This movement was so exciting that we wanted to help make it
              happen in better and bigger ways. So we wrote a book about the
              idea and started to put it all into practice. First, with
              digitally fabricated houses - the Wikihouse - designed to be
              affordable to many more people. But we didn&apos;t stop there. And
              we didn&apos;t stop asking what if...
            </Trans>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>
              What if &quot;hardware&quot; products were developed like
              software?
            </Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            <Trans>
              With open innovation and a more agile mindset and methods making
              the design and development of physical things better, faster and
              in tune with people&apos;s needs?
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              And what if the usually expensive and complex tools needed to do
              this collaboratively were made accessible to anyone with a great
              idea, a laptop and an internet connection?
            </Trans>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>And while we’re at it...</Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            {/* TODO: add this source responsive images control for the image component */}
            <picture>
              <ImageStyled src={staticURL('/assets/home/why-not.jpg')} />
            </picture>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>
              Why not make this happen on a different kind of social platform?
            </Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            <Trans>
              One designed for productivity not killing time? For creating
              things, not consuming things?
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              Where a community from around the world could innovate together to
              develop meaningful and useful products with local materials and
              resources that solved real-world problems?
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              Where enterprises could tap into the collective ingenuity,
              expertise and talents of a global community of product developers,
              opening their own innovation to fresh ideas and possibilities?
            </Trans>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>Those were the questions. Wikifactory was our answer.</Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            {/* TODO: add this source responsive images control for the image component */}
            <picture>
              <ImageStyled
                src={staticURL('/assets/home/why-this-matters.jpg')}
              />
            </picture>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              What if we built a single, online infrastructure... where a farmer
              in rural Africa could fabricate a vital replacement part for a
              tractor faster and cheaper locally. Products for crisis relief
              could be produced on the ground, avoiding delays and removing
              costly shipping and inventories.
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              Diverse communities around the world could make what they needed,
              with locally available materials and resources, and become more
              self-sufficient. They could solve problems collaboratively. Be
              inspired by, and learn from, others. Build on each others&apos;
              work by re-using components. Not re-inventing the wheel.
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              The environment would benefit too, because we could adopt a more
              circular industrial model to the one that is rapidly consuming our
              planet&apos;s resources.
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              And Industry would become a more level playing field. New entrants
              could create new ideas and greater competition. Resulting in
              better every-day products for everyone.
            </Trans>
          </Paragraph>
          <HeadingStyled level={2} size='large'>
            ―
          </HeadingStyled>
          <Paragraph size='medium'>
            {/* TODO: add this source responsive images control for the image component */}
            <picture>
              <ImageStyled src={staticURL('/assets/home/whats-next.jpg')} />
            </picture>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>Something that will turn manufacturing on its head.</Trans>
          </HeadingStyled>
          <Paragraph size='medium'>
            <Trans>We call it The Internet of Production.</Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              The internet and the sharing economy has already transformed
              participation in ideas, influence, and opinion. But the Internet
              of Production will transform global industry.
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              Industry 4.0 has been called a game-changer for manufacturing.
              Although it might digitise the systems, it won’t change the
              paradigm or the shape of global supply chains.
            </Trans>
          </Paragraph>
          <Paragraph size='medium'>
            <Trans>
              What we propose will. Disrupting not one industry, but industry
              itself. Creating a more inclusive, open, sustainable and resilient
              industrial model. A single online infrastructure for global
              innovation and local production.
            </Trans>
          </Paragraph>
          <HeadingStyled level={2} size='medium'>
            <Trans>
              The future is in the making. With just a laptop and an internet
              connection, we can build that future today.
            </Trans>
          </HeadingStyled>
        </Container>
        {!clientStore.signedInProfile && <Join />}
      </>
    );
  }
}

OurStory.propTypes = {
  clientStore: PropTypes.object,
};
